import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedChip as Chip, LocalizedTooltip as Tooltip } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import {
  AutoAwesomeOutlined,
  ExpandMore,
  FamilyRestroomOutlined,
  HealthAndSafetyOutlined,
  HomeWorkOutlined,
  InfoOutlined,
  SchoolOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import type {
  BNNGuidanceCategoryKey,
  BNNGuidanceNotice,
  BNNGuidanceResult,
  BNNGuidanceRuleMatch,
} from './bnnGuidance.types';

export interface BNNInterpretationGuidanceProps {
  result?: BNNGuidanceResult | null;
  loading?: boolean;
  error?: string | null;
  defaultCategory?: 'all' | BNNGuidanceCategoryKey;
  onRuleOpen?: (rule: BNNGuidanceRuleMatch) => void;
}

const CATEGORY_ICONS: Record<BNNGuidanceCategoryKey, React.ReactNode> = {
  physical: <HealthAndSafetyOutlined fontSize="small" />,
  career: <SchoolOutlined fontSize="small" />,
  relationships: <FamilyRestroomOutlined fontSize="small" />,
  surroundings: <HomeWorkOutlined fontSize="small" />,
  life: <AutoAwesomeOutlined fontSize="small" />,
};

function noticeSeverity(key: BNNGuidanceNotice['key']): 'info' | 'warning' {
  return key === 'medical' || key === 'lifespan' ? 'warning' : 'info';
}

function sensitivityLabel(value: string, ui: Record<string, string>): string {
  const labels: Record<string, string> = {
    medical: ui.healthIndication,
    'medical-third-party': ui.familyHealthIndication,
    'medical-safety': ui.safetyAndHealth,
    'mental-health': ui.emotionalWellbeing,
    fertility: ui.familyPlanning,
    financial: ui.financialPattern,
    career: ui.careerPattern,
    education: ui.educationPattern,
    relationship: ui.relationshipPattern,
    'relationship-third-party': ui.familyRelationshipPattern,
    'lifespan-third-party': ui.traditionalFamilyObservation,
    safety: ui.safetyIndication,
    property: ui.propertyPattern,
    relocation: ui.relocationPattern,
    'traditional-observation': ui.traditionalObservation,
  };
  return labels[value] || value.replace(/-/g, ' ');
}

function RuleCard({
  rule,
  ui,
  onOpen,
}: {
  rule: BNNGuidanceRuleMatch;
  ui: Record<string, string>;
  onOpen?: (rule: BNNGuidanceRuleMatch) => void;
}) {
  const caution = rule.nature === 'caution';

  return (
    <Accordion
      disableGutters
      elevation={0}
      onChange={(_, expanded) => {
        if (expanded) onOpen?.(rule);
      }}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px !important',
        overflow: 'hidden',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls={`${rule.id}-content`}
        id={`${rule.id}-header`}
        sx={{ px: { xs: 1.5, sm: 2 }, py: 0.5 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start" width="100%" pr={1}>
          <Box sx={{ mt: 0.4, color: caution ? 'warning.main' : 'primary.main' }}>
            {caution ? <WarningAmberOutlined fontSize="small" /> : CATEGORY_ICONS[rule.category]}
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography fontWeight={750} lineHeight={1.35}>
              {rule.title}
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.8}>
              <Chip size="small" label={ui[rule.nature] || rule.nature} variant="outlined" />
              <Tooltip
                title={
                  rule.matchQuality === 'exact'
                    ? ui.exactTooltip
                    : ui.contextualTooltip
                }
              >
                <Chip
                  size="small"
                  label={rule.matchQuality === 'exact' ? ui.exactRuleMatch : ui.contextualMatch}
                  variant="outlined"
                />
              </Tooltip>
              <Chip size="small" label={sensitivityLabel(rule.sensitivity, ui)} variant="outlined" />
              {rule.conditionalApplicability && (
                <Chip size="small" label={ui.checkApplicability} variant="outlined" />
              )}
            </Stack>
          </Box>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pt: 0, pb: 2 }}>
        <Divider sx={{ mb: 1.75 }} />
        <Stack spacing={1.75}>
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              {ui.whyThisAppears}
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
              {rule.factors.map((factor) => (
                <Typography component="li" variant="body2" key={factor}>
                  {factor}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              {ui.interpretation}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.7 }}>
              {rule.interpretation}
            </Typography>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              {ui.practicalGuidance}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.7 }}>
              {rule.guidance}
            </Typography>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function BNNInterpretationGuidance({
  result,
  loading = false,
  error,
  defaultCategory = 'all',
  onRuleOpen,
}: BNNInterpretationGuidanceProps) {
  const [selectedCategory, setSelectedCategory] = useState<'all' | BNNGuidanceCategoryKey>(
    defaultCategory,
  );

  useEffect(() => {
    if (
      selectedCategory !== 'all' &&
      result &&
      !result.categories.some((category) => category.key === selectedCategory)
    ) {
      setSelectedCategory('all');
    }
  }, [result, selectedCategory]);

  const visibleRules = useMemo(() => {
    if (!result) return [];
    if (selectedCategory === 'all') return result.matchedRules;
    return result.matchedRules.filter((rule) => rule.category === selectedCategory);
  }, [result, selectedCategory]);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={28} />
        <Typography mt={1.5} color="text.secondary">
          Checking BNN interpretation rules…
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!result) {
    return <Alert severity="info">BNN guidance data is not available for this chart.</Alert>;
  }

  const ui = result.ui;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: { xs: 1.75, sm: 2.25 }, borderRadius: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoOutlined color="primary" />
              <Typography variant="h6" fontWeight={800}>
                {ui.title}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {ui.subtitle}
            </Typography>
          </Box>

          <Stack direction="row" gap={0.75} flexWrap="wrap">
            <Chip label={`${result.summary.matched} ${ui.matched}`} />
            <Chip label={`${result.summary.exact} ${ui.exact}`} variant="outlined" />
            {result.summary.contextual > 0 && (
              <Chip label={`${result.summary.contextual} ${ui.contextual}`} variant="outlined" />
            )}
          </Stack>
        </Stack>
      </Paper>

      {result.notices.map((notice) => (
        <Alert key={notice.key} severity={noticeSeverity(notice.key)}>
          {notice.text}
        </Alert>
      ))}

      <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip
          clickable
          label={`${ui.all} (${result.summary.matched})`}
          color={selectedCategory === 'all' ? 'primary' : 'default'}
          variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
          onClick={() => setSelectedCategory('all')}
        />
        {result.categories.map((category) => (
          <Chip
            key={category.key}
            clickable
            icon={CATEGORY_ICONS[category.key] as React.ReactElement}
            label={`${category.label} (${category.count})`}
            color={selectedCategory === category.key ? 'primary' : 'default'}
            variant={selectedCategory === category.key ? 'filled' : 'outlined'}
            onClick={() => setSelectedCategory(category.key)}
          />
        ))}
      </Stack>

      {visibleRules.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
          <Typography fontWeight={700}>{ui.noMatchCategory}</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.75}>
            {ui.incompleteJudgement}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.25}>
          {visibleRules.map((rule) => (
            <RuleCard key={rule.id} rule={rule} ui={ui} onOpen={onRuleOpen} />
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary">
        {ui.ruleEngineVersion} {result.version}. {ui.completeChartNote}
      </Typography>
    </Stack>
  );
}
